import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { api } from "@/api";

const Footer = () => {
  const { lang, t } = useLanguage();
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter/subscribe", { email: nlEmail, lang });
      setNlDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <footer data-testid="main-footer" className="bg-forest text-cream">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-cream/15">
          <div className="md:col-span-5">
            <p className="font-serif text-4xl md:text-5xl leading-tight font-medium max-w-md" data-testid="footer-tagline">
              {t("footer.tagline")}
            </p>
            <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mt-12 mb-4">{t("footer.newsletterTitle")}</p>
            {nlDone ? (
              <p data-testid="newsletter-success" className="text-sm text-clay">{t("footer.newsletterThanks")}</p>
            ) : (
              <form onSubmit={subscribe} className="flex max-w-md" data-testid="newsletter-form">
                <input
                  data-testid="newsletter-email-input"
                  required
                  type="email"
                  value={nlEmail}
                  onChange={(e) => setNlEmail(e.target.value)}
                  placeholder={t("footer.newsletterPlaceholder")}
                  className="flex-1 min-w-0 bg-transparent border border-cream/25 rounded-l-full px-5 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-clay transition-colors duration-300"
                />
                <button data-testid="newsletter-submit" className="bg-clay text-cream px-6 py-3 rounded-r-full text-sm font-medium hover:bg-clay-hover transition-colors duration-300 shrink-0">
                  {t("footer.newsletterBtn")}
                </button>
              </form>
            )}
          </div>
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-6">{t("footer.explore")}</p>
            <div className="flex flex-col gap-3">
              <Link to="/" data-testid="footer-link-home" className="text-sm text-cream/80 hover:text-clay transition-colors duration-300">{t("nav.home")}</Link>
              <Link to="/about" data-testid="footer-link-about" className="text-sm text-cream/80 hover:text-clay transition-colors duration-300">{t("nav.about")}</Link>
              <Link to="/campaigns" data-testid="footer-link-campaigns" className="text-sm text-cream/80 hover:text-clay transition-colors duration-300">{t("nav.campaigns")}</Link>
              <Link to="/membership" data-testid="footer-link-membership" className="text-sm text-cream/80 hover:text-clay transition-colors duration-300">{t("nav.membership")}</Link>
              <Link to="/contact" data-testid="footer-link-contact" className="text-sm text-cream/80 hover:text-clay transition-colors duration-300">{t("nav.contact")}</Link>
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-6">{t("footer.contactTitle")}</p>
            <div className="flex flex-col gap-3 text-sm text-cream/80">
              <a href="mailto:contact@future-gen.eu" data-testid="footer-email" className="hover:text-clay transition-colors duration-300 inline-flex items-center gap-1">
                contact@future-gen.eu <ArrowUpRight size={14} />
              </a>
              <span>Luxembourg</span>
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-6">{t("footer.legalTitle")}</p>
            <p className="text-sm text-cream/70 leading-relaxed" data-testid="footer-legal">{t("footer.legal")}</p>
            <p className="text-sm text-cream/70 mt-3">IBAN BE23 9054 3388 9491 · BIC TRWIBEB1XXX</p>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs text-cream/50">© 2026 Future Gen ASBL — <a href="http://future-gen.eu" data-testid="footer-domain" className="hover:text-clay transition-colors duration-300">future-gen.eu</a> · {t("footer.rights")}</p>
          <p className="font-serif italic text-cream/60 text-lg">Future Gen ASBL</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
